import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

import GoogleDebugPanel from "@/components/google/GoogleDebugPanel";
import GoogleStatusCard from "@/components/google/GoogleStatusCard";
import TechnicianCalendarTable from "@/components/google/TechnicianCalendarTable";

/**
 * Pestaña de configuración Google Calendar.
 *
 * - Conecta una única cuenta Google vía OAuth.
 * - Lista todos los calendarios de esa cuenta.
 * - Permite asignar uno a cada técnico (sin duplicados).
 *
 * Orquesta tres sub-componentes: GoogleDebugPanel, GoogleStatusCard y
 * TechnicianCalendarTable.
 */
export default function GoogleCalendarTab({ disabled }) {
  const [status, setStatus] = useState({ connected: false });
  const [calendars, setCalendars] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRow, setSavingRow] = useState(null);
  const [debug, setDebug] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [st, users, dbg] = await Promise.all([
        api.get("/google/status"),
        api.get("/users"),
        api.get("/google/debug").catch(() => ({ data: null })),
      ]);
      setStatus(st.data);
      setDebug(dbg.data);
      // Técnicos = usuarios con rol "technician" (o que ya tengan calendar asignado)
      const techs = (users.data || []).filter(
        (u) => (u.roles || []).includes("technician") || u.google_calendar_id
      );
      setTechnicians(techs);
      if (st.data.connected) {
        try {
          const cals = await api.get("/google/calendars");
          setCalendars(cals.data.calendars || []);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "No se pudieron leer los calendarios");
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") {
      toast.success("Cuenta de Google conectada correctamente");
      const url = new URL(window.location);
      url.searchParams.delete("google");
      window.history.replaceState({}, "", url);
    } else if (params.get("google") === "error") {
      const detail = params.get("detail") || "";
      const desc = params.get("desc") || "";
      toast.error(`Google: ${detail}${desc ? " — " + desc : ""}`, { duration: 10000 });
      const url = new URL(window.location);
      url.searchParams.delete("google");
      url.searchParams.delete("detail");
      url.searchParams.delete("desc");
      window.history.replaceState({}, "", url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      const r = await api.get("/google/auth-url");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar OAuth");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar la cuenta de Google? Se borrarán las asignaciones de calendario en los técnicos.")) return;
    try {
      await api.delete("/google/disconnect");
      toast.success("Cuenta desconectada");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error desconectando");
    }
  };

  const assign = async (userId, calendarId, calendarName) => {
    setSavingRow(userId);
    try {
      await api.post("/google/assign-calendar", {
        user_id: userId,
        calendar_id: calendarId,
        calendar_name: calendarName,
      });
      toast.success(calendarId ? "Calendario asignado" : "Calendario desasignado");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo asignar");
    } finally {
      setSavingRow(null);
    }
  };

  const updateColor = async (userId, calendarId, calendarName, color) => {
    setSavingRow(userId);
    try {
      await api.post("/google/assign-calendar", {
        user_id: userId,
        calendar_id: calendarId,
        calendar_name: calendarName,
        calendar_color: color,
      });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el color");
    } finally {
      setSavingRow(null);
    }
  };

  if (loading) return <div className="text-sm text-[#525252]">Cargando…</div>;

  return (
    <div className="space-y-4">
      <GoogleDebugPanel debug={debug} />
      <GoogleStatusCard
        status={status}
        disabled={disabled}
        onConnect={connect}
        onDisconnect={disconnect}
        onRefresh={refresh}
      />
      {status.connected && (
        <TechnicianCalendarTable
          technicians={technicians}
          calendars={calendars}
          disabled={disabled}
          savingRow={savingRow}
          onAssign={assign}
          onUpdateColor={updateColor}
        />
      )}
    </div>
  );
}
